import { useState, useEffect } from 'react'
import { requireClient } from '@/lib/matrix/client'
import { useAuthStore } from '@/lib/store/auth-store'
import { extractLocation, extractLocationAddress, geocodeAddress, aiLocate, type GeoCoords } from '@/lib/utils/location'
import type { SerializedEvent } from '@/lib/matrix/events'

interface MessageActionsProps {
  event: SerializedEvent
  roomId: string
  aiEnabled?: boolean
  onReply: () => void
  onEdit: () => void
  onReact: () => void
  onForward: () => void
  onThread?: () => void
  onShowMap?: (coords: GeoCoords) => void
  onInfo: () => void
}

export function MessageActions({ event, roomId, aiEnabled, onReply, onEdit, onReact, onForward, onThread, onShowMap, onInfo }: MessageActionsProps) {
  const userId = useAuthStore((s) => s.userId)
  const isOwn = event.sender === userId
  const directCoords = extractLocation(event.content)
  const [geocoded, setGeocoded] = useState<GeoCoords | null>(null)

  useEffect(() => {
    if (directCoords || !aiEnabled) return
    const body = (event.content.body as string) ?? ''
    if (body.length < 20) return

    aiLocate(body, userId).then((result) => {
      if (result) { setGeocoded(result); return }
      const address = extractLocationAddress(body)
      if (address) geocodeAddress(address).then(setGeocoded)
    })
  }, [event.eventId, directCoords, userId, aiEnabled])

  const coords = directCoords ?? geocoded

  const handleDelete = async () => {
    const client = requireClient()
    await client.redactEvent(roomId, event.eventId)
  }

  return (
    <div className="msg-actions">
      <ActionButton title="React" onClick={onReact}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <line x1="9" y1="9" x2="9.01" y2="9" />
          <line x1="15" y1="9" x2="15.01" y2="9" />
        </svg>
      </ActionButton>
      <ActionButton title="Reply" onClick={onReply}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 17 4 12 9 7" />
          <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
        </svg>
      </ActionButton>
      {onThread && (
        <ActionButton title="Reply in thread" onClick={onThread}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </ActionButton>
      )}
      <ActionButton title="Forward" onClick={onForward}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 17 20 12 15 7" />
          <path d="M4 18v-2a4 4 0 0 1 4-4h12" />
        </svg>
      </ActionButton>
      {coords && onShowMap && (
        <ActionButton title="Show on map" onClick={() => onShowMap(coords)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        </ActionButton>
      )}
      {isOwn && (
        <ActionButton title="Edit" onClick={onEdit}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </ActionButton>
      )}
      {isOwn && (
        <ActionButton title="Delete" onClick={handleDelete}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </ActionButton>
      )}
      <ActionButton title="Info" onClick={onInfo}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </ActionButton>
    </div>
  )
}

function ActionButton({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={title} className="msg-action">
      {children}
    </button>
  )
}
